import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
  type CellContext, type ColumnDef, type Header, type HeaderContext, type RowData, type SortingState,
} from "@tanstack/react-table";
import { useId, useState, type MouseEvent, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { EmptyState } from "./list-state";
import { ListPagination, type ListPaginationProps } from "./list-pagination";
import { Skeleton } from "./skeleton";
import { useQueryErrorToast } from "./use-query-error-toast";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    separator?: boolean;
    /** Return null to omit a cell covered by rowSpan or colSpan. */
    td?(context: CellContext<TData, TValue>): TdHTMLAttributes<HTMLTableCellElement> | null;
    /** className applies to the header label/control; other attributes apply to th. */
    th?(context: HeaderContext<TData, TValue>): ThHTMLAttributes<HTMLTableCellElement> | null;
    headerAction?: ReactNode;
  }
}

function TableHeader<T>({ header }: Readonly<{ header: Header<T, unknown> }>) {
  const props = header.column.columnDef.meta?.th?.(header.getContext());
  if (props === null) return null;
  const { className, ...attributes } = props ?? {};
  const sorted = header.column.getIsSorted();
  const sortable = !header.isPlaceholder && header.column.getCanSort();
  const action = header.column.columnDef.meta?.headerAction;
  const label = flexRender(header.column.columnDef.header, header.getContext());
  return <th
    colSpan={header.colSpan}
    scope={header.subHeaders.length ? "colgroup" : "col"}
    {...attributes}
    aria-sort={sortable ? sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none" : undefined}
    className={cn("border-b border-border bg-muted/50 p-1", header.column.columnDef.meta?.separator && "border-r")}
  >
    {header.isPlaceholder ? null : <div className="flex items-center gap-2">
      {sortable ? <Button
        variant="ghost" size="sm"
        className={cn("block min-w-0 flex-1 text-left", className)}
        onClick={header.column.getToggleSortingHandler()}
      >{label}{sorted ? <span aria-hidden="true">{sorted === "asc" ? " ↑" : " ↓"}</span> : null}</Button>
        : <div className={cn("min-w-0 flex-1 whitespace-nowrap px-2.5 py-1.5 text-left text-xs font-medium text-muted-foreground", className)}>{label}</div>}
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>}
  </th>;
}

// A row is a navigation target, but its embedded controls keep their own actions.
function isRowClick(event: MouseEvent<HTMLTableRowElement>): boolean {
  if (event.defaultPrevented || !(event.target instanceof Element)) return false;
  const control = event.target.closest('a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="combobox"], [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])');
  return !control || control === event.currentTarget;
}

export function DataTable<T>({ data, columns, empty, loading, refreshing = false, error, onRetry, pagination, className, header, bare = false, onRowSelect, initialSorting = [] }: Readonly<{
  data: T[];
  columns: ColumnDef<T>[];
  initialSorting?: SortingState;
  empty?: string;
  loading?: string;
  refreshing?: boolean;
  error?: unknown;
  onRetry?(): void;
  pagination?: ListPaginationProps;
  className?: string;
  header?: ReactNode;
  /** Omit the card shell when the table sits in an existing panel. */
  bare?: boolean;
  onRowSelect?(row: T): void;
}>) {
  const { t } = useTranslation();
  const notificationId = useId();
  useQueryErrorToast(refreshing ? null : error, t("workspace.loadingListError"), notificationId);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const table = useReactTable({
    data, columns, state: { sorting }, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
  });
  return <div aria-busy={loading !== undefined || refreshing} className="relative min-w-0">
    <div className={cn("overflow-x-auto", !bare && "table-shell rounded-xl border border-border bg-card", className)}>
    {header ? <div className="border-b border-border bg-muted/30 p-4">{header}</div> : null}
    {loading !== undefined ? <span className="sr-only" role="status">{loading}</span> : null}
    <table className="w-full min-w-[760px] border-collapse text-left">
      <thead>{table.getHeaderGroups().map(group => <tr key={group.id}>
        {group.headers.map(header => <TableHeader key={header.id} header={header} />)}
      </tr>)}</thead>
      <tbody>
        {loading !== undefined ? Array.from({ length: 5 }, (_, index) => <tr key={index} aria-hidden="true">
          {table.getVisibleLeafColumns().map(column => <td key={column.id} className={cn("border-b border-border px-3.5 py-3", column.columnDef.meta?.separator && "border-r")}><Skeleton className="h-4 w-3/4" /></td>)}
        </tr>) : table.getRowModel().rows.map(row => <tr
          key={row.id}
          className={cn("transition-colors hover:bg-accent/50", onRowSelect && "cursor-pointer focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30")}
          tabIndex={onRowSelect ? 0 : undefined}
          onClick={onRowSelect ? event => { if (isRowClick(event)) onRowSelect(row.original); } : undefined}
          onKeyDown={onRowSelect ? event => {
            if (!event.defaultPrevented && event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              onRowSelect(row.original);
            }
          } : undefined}
        >{row.getVisibleCells().map(cell => {
          const props = cell.column.columnDef.meta?.td?.(cell.getContext());
          if (props === null) return null;
          return <td {...props} key={cell.id} className={cn("border-b border-border px-3.5 py-3 align-top text-sm text-foreground", cell.column.columnDef.meta?.separator && "border-r", props?.className)}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>;
        })}</tr>)}
        {loading === undefined && table.getRowModel().rows.length === 0 ? <tr>
          <td className="border-b border-border px-4 text-center text-sm text-muted-foreground" colSpan={table.getVisibleLeafColumns().length}>
            <EmptyState compact title={error ? t("workspace.loadingListError") : empty ?? t("workspace.noData")} />
            {error && onRetry ? <Button variant="outline" size="sm" className="mb-4" onClick={onRetry}>{t("workspace.retry")}</Button> : null}
          </td>
        </tr> : null}
      </tbody>
    </table></div>
    {pagination ? <ListPagination {...pagination} /> : null}
  </div>;
}
